import { useState, useMemo, useCallback } from 'react';

/**
 * Reusable pagination hook.
 *
 * @param {Array} items - The full filtered/sorted array to paginate
 * @param {number} initialPageSize - Default page size (default: 10)
 * @param {number} initialPage - Page to start on (default: 1) — lets a caller
 *   restore the page a user was on, e.g. returning from a detail view
 * @returns {{ currentPage, pageSize, totalPages, paginated, setCurrentPage, setPageSize }}
 */
function usePagination(items, initialPageSize = 10, initialPage = 1) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(items.length / pageSize)),
    [items.length, pageSize]
  );

  // Clamp page if it goes out of range (e.g. after filter change shrinks list)
  const safePage = Math.min(currentPage, totalPages);

  const paginated = useMemo(
    () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize]
  );

  const setPageSize = useCallback((newSize) => {
    setPageSizeState(newSize);
    setCurrentPage(1);
  }, []);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage: safePage,
    pageSize,
    totalPages,
    paginated,
    setCurrentPage,
    setPageSize,
    resetPage,
  };
}

export default usePagination;
